/**
 * Hook for managing Kanban board card/player operations
 * @remarks
 * Extracted to reduce cognitive complexity.
 * Provides handler functions for player add, move, remove, and drop operations.
 */
import { arrayMove } from "@dnd-kit/sortable";
import { useCallback, useEffect, useRef } from "react";

import { PlayerDragData } from "../types/types";
import {
  gateRequiresPrompt,
  resolveStageMoveGate,
} from "../utils/stageMoveGate";
import {
  calculateNewPosition,
  sortCardsForViewer,
} from "./shared/kanbanBoardUtils";
import type useKanbanBoardMutations from "./useKanbanBoardMutations";
import type { StageMoveGate } from "../utils/stageMoveGate";

import { resolvePipelineError } from "../utils/pipelineErrorMap";

import { AnalyticsTracker } from "@/features/analytics";
import type { TCardMoveMethod } from "@/features/analytics";
import type {
  KanbanBoard,
  KanbanCard,
  KanbanCardOutcome,
  KanbanPhase,
  ListKanbanCardsQuery,
} from "@/generated/graphql";
import { KanbanSourceType } from "@/generated/graphql";

/**
 * Trailing debounce window (ms) for coalescing rapid rank reorders into a
 * single background save per phase (SE-4687). Rapid drags within this window
 * collapse to one `updateKanbanCardPositions` call carrying the final order.
 */
export const RANK_SAVE_DEBOUNCE_MS = 400;

/**
 * Type for refetch cards result
 */
type RefetchCardsResult = { data?: ListKanbanCardsQuery };

/**
 * Everything the prompt needs to describe the move it is gating (SE-5495).
 * Passed to the injected resolver so the presentation layer can render a
 * specific message ("Move Player 1 into Offer Made") without reaching back
 * into board state.
 */
export interface StageMoveRequest {
  /** What the move must collect before it may proceed. */
  readonly gate: StageMoveGate;
  /** The card being moved. */
  readonly cardId: string;
  /** The player on that card. */
  readonly playerId: string;
  /** Display name of the player, when known. */
  readonly playerName?: string;
  /** Destination stage id. */
  readonly toPhaseId: string;
  /** Destination stage name, for the prompt copy. */
  readonly toPhaseName: string;
  /** Origin stage name, for the prompt copy. */
  readonly fromPhaseName?: string;
  /**
   * SE-5496: the board template's ordered, valid terminal outcomes. The prompt
   * renders the outcome selector from exactly this set when
   * `gate.requiresOutcome` is true — never a hardcoded per-template list. Empty
   * for a non-terminal move.
   */
  readonly validOutcomes: readonly KanbanCardOutcome[];
}

/**
 * What the user supplied in the prompt. `null` from the resolver means the
 * user cancelled and the move must be abandoned entirely.
 */
export interface StageMoveDecision {
  /** The reason typed by the mover; recorded on the audit trail. */
  readonly rationale?: string;
  /**
   * SE-5496: the terminal outcome the user selected. Present only for a
   * terminal move (`gate.requiresOutcome`); routes the move to the close
   * mutation instead of a plain stage move.
   */
  readonly outcome?: KanbanCardOutcome;
}

/**
 * Resolver injected by the presentation layer to satisfy a gate.
 * Resolves with the user's answer, or `null` if they cancelled.
 */
export type StageMoveDecisionResolver = (
  request: StageMoveRequest
) => Promise<StageMoveDecision | null>;

/**
 * Dependencies for the card operations hook
 */
interface CardOperationsDeps {
  /** Current kanban board */
  board: KanbanBoard | null;
  /** Current phases */
  phases: KanbanPhase[];
  /** Current cards */
  cards: KanbanCard[];
  /** Mutation functions */
  mutations: ReturnType<typeof useKanbanBoardMutations>;
  /** Refetch cards function */
  refetchCards: () => Promise<unknown>;
  /** Error setter */
  setError: (error: string | null) => void;
  /** Saving state setter */
  setIsSaving: (saving: boolean) => void;
  /** Record a pending card move to prevent UI flicker during cache rollback (SE-4272) */
  onMoveStart: (
    cardId: string,
    toPhaseId: string,
    phaseName: string,
    moveId: string
  ) => void;
  /** Clear a pending card move after mutation resolves (SE-4272) */
  onMoveEnd: (cardId: string, moveId: string) => void;
  /**
   * Apply an optimistic rank override for a batch of cards synchronously
   * (inside flushSync) so a reorder lands instantly while its network save is
   * debounced (SE-4687). Each entry pins a card's displayed position.
   */
  onRankStart: (
    entries: { cardId: string; position: number }[],
    batchId: string
  ) => void;
  /**
   * Clear a rank override batch once its debounced save settles (SE-4687).
   * batchId scoping ensures a newer window's override is never wiped by an
   * older save's cleanup.
   */
  onRankEnd: (batchId: string) => void;
  /**
   * SE-5495: satisfy a stage-move gate before the move is committed. Injected
   * rather than owned here so this hook stays presentation-free and the same
   * gate can be driven by any surface (board drag today; the player drawer and
   * profile in SE-5494 and its sibling).
   *
   * Optional on purpose — a caller that has not wired a prompt yet must still
   * be able to move cards. The gate degrades OPEN, never closed.
   */
  requestStageMoveDecision?: StageMoveDecisionResolver;
}

/**
 * Return type for the useKanbanCardOperations hook
 */
interface CardOperationsResult {
  /** Whether this is a roster board */
  isRosterBoard: boolean;
  /** Current player count */
  playerCount: number;
  /** Move player to phase */
  movePlayerToPhase: (
    playerId: string,
    fromPhaseId: string,
    toPhaseId: string,
    insertionIndex?: number,
    method?: TCardMoveMethod
  ) => Promise<void>;
  /** Add player to board */
  addPlayerToBoard: (
    playerId: string,
    phaseId: string,
    playerData: PlayerDragData
  ) => Promise<void>;
  /**
   * Remove a player's card from the board.
   *
   * SE-5500: takes the OPTIONAL removal rationale recorded on the REMOVED
   * audit row. Blank/whitespace normalises to `null` server-side, so a blank
   * reason is sent as `undefined` rather than as an empty string. REJECTS when
   * the removal failed, so a caller holding a confirmation dialog open (and
   * the reason the user typed) can tell success from failure.
   */
  removePlayerFromBoard: (
    playerId: string,
    rationale?: string
  ) => Promise<void>;
  /** Handle player drop */
  handlePlayerDrop: (targetPhaseId: string, playerData: PlayerDragData) => void;
  /**
   * Reorder a card within its phase by moving the active player's card into
   * the slot occupied by the over player's card, renumbering the whole phase
   * to a contiguous 0-based sequence (SE-4685 rank drag-reorder).
   */
  reorderCardsInPhase: (
    phaseId: string,
    activePlayerId: string,
    overPlayerId: string
  ) => void;
  /**
   * SE-5496: reopen a closed card into a REQUIRED working (non-terminal) stage.
   * Clears the terminal state; the backend writes the REOPENED audit entry with
   * the optional rationale. Rejects on a real error (after setting it) so the
   * dialog can stay open; a CARD_ALREADY_CLOSED/not-closed refresh and a
   * no-access signal are absorbed here.
   */
  reopenCard: (
    cardId: string,
    targetPhaseId: string,
    rationale?: string
  ) => Promise<void>;
}

/**
 * Hook for kanban card/player operations
 * @param deps - Dependencies for operations
 * @returns Object with player move, add, remove, and drop handlers plus board state flags
 */
const useKanbanCardOperations = (
  deps: CardOperationsDeps
): CardOperationsResult => {
  const {
    board,
    phases,
    cards,
    mutations,
    refetchCards,
    setError,
    setIsSaving,
    onMoveStart,
    onMoveEnd,
    onRankStart,
    onRankEnd,
    requestStageMoveDecision,
  } = deps;

  // Computed values for roster board
  const isRosterBoard = board?.sourceType === KanbanSourceType.Roster;
  const playerCount = cards.length;

  // Monotonic counter used to scope onMoveEnd cleanup to its originating
  // move so a rapid second drag of the same card is never wiped by the
  // first drag's deferred cleanup.
  const moveCounterRef = useRef(0);

  // Per-phase debounced rank-save window (SE-4687). Rapid reorders in the same
  // phase compound in `workingOrder` (the current card objects in display
  // order) and coalesce into a single background save carrying the final
  // order. `batchId` scopes the optimistic override so a newer window is never
  // cleared by an older save; `timer` implements the trailing debounce.
  const rankCounterRef = useRef(0);
  const rankWorkingOrderRef = useRef(new Map<string, KanbanCard[]>());
  const rankBatchIdRef = useRef(new Map<string, string>());
  const rankTimerRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // SE-5496: interpret a thrown pipeline error and drive the caller's reaction.
  // Returns `true` when the error was absorbed (refresh or silent no-access) and
  // the caller should stop; rethrows/records a real domain error so the caller's
  // surface (dialog, board) can respond. Centralised so the close and reopen
  // paths share one interpretation of `extensions.code`.
  const handlePipelineError = useCallback(
    async (err: unknown): Promise<boolean> => {
      const resolution = resolvePipelineError(err);
      if (resolution.kind === "refresh") {
        await refetchCards();
        return true;
      }
      if (resolution.kind === "silent") return true;
      setError(resolution.message);
      throw err;
    },
    [refetchCards, setError]
  );

  // SE-5496: close a card into a terminal stage. Extracted from
  // `movePlayerToPhase` so the move path stays under the cognitive-complexity
  // ceiling. No optimistic override / onMoveStart pin — the terminal stage
  // renders no cards (closed cards live in the Closed area), so the SE-4272
  // column-pin machinery does not apply; a ListKanbanCards refetch reconciles.
  const closeCardToTerminal = useCallback(
    async (
      cardId: string,
      outcome: KanbanCardOutcome,
      reason: string,
      targetPhaseId: string
    ): Promise<boolean> => {
      try {
        await mutations.closeCardMutation({
          // Always send an explicit target phase — the backend never defaults it.
          variables: { input: { cardId, outcome, reason, targetPhaseId } },
        });
        return true;
      } catch (err) {
        await handlePipelineError(err);
        // SE-5508: reached only when `handlePipelineError` ABSORBED the error
        // (its "refresh" / "silent" kinds return rather than rethrow). Absorbed
        // is not succeeded — CARD_ALREADY_CLOSED refreshes the board without
        // this call having closed anything, and a below-Manager caller's
        // code-less NotFound is silently declined. Both must report `false` so
        // the caller does not emit a `card_moved` for a close that never
        // happened.
        return false;
      }
    },
    [mutations, handlePipelineError]
  );

  /**
   * SE-5508: close a card into a terminal stage and report the resulting
   * `card_moved`.
   *
   * Extracted from `movePlayerToPhase` so that path stays inside the
   * cognitive-complexity ceiling. The emission is gated on the close actually
   * committing: `closeCardToTerminal` returns false when `handlePipelineError`
   * ABSORBED the failure (CARD_ALREADY_CLOSED refreshes the board; a
   * below-Manager caller's code-less NotFound is silently declined), neither of
   * which moved the card.
   *
   * A close IS a stage change for telemetry, and it is the only path that can
   * carry an `outcome`.
   * @param card - The card being closed.
   * @param outcome - The terminal outcome the user selected.
   * @param reason - The rationale recorded on the audit trail.
   * @param toPhaseId - The terminal stage being closed into.
   * @param method - Which surface initiated the move.
   */
  const closeToTerminalAndReport = useCallback(
    async (
      card: KanbanCard,
      outcome: KanbanCardOutcome,
      reason: string,
      toPhaseId: string,
      method: TCardMoveMethod
    ): Promise<void> => {
      const closed = await closeCardToTerminal(
        card.id,
        outcome,
        reason,
        toPhaseId
      );
      if (!closed || !board) return;

      void AnalyticsTracker.pipeline.logCardMoved({
        from: card.kanbanPhaseId,
        to: toPhaseId,
        method,
        sensitive: true,
        outcome,
        board_id: board.id,
      });
    },
    [closeCardToTerminal, board]
  );

  const movePlayerToPhase = useCallback(
    async (
      playerId: string,
      _fromPhaseId: string,
      toPhaseId: string,
      insertionIndex?: number,
      // SE-5508: which surface initiated the move. Defaults to "drag" because
      // every drag entry point (`handlePlayerDrop`, `addPlayerToBoard`'s
      // existing-card short-circuit) reaches this function positionally; the
      // ONE non-drag caller — the preview drawer's stage picker — passes
      // "stage_picker" explicitly. Instrumenting here rather than at the two
      // call sites is what keeps `card_moved` exactly-once: this is the single
      // shared move service both surfaces route through (AC4/SE-5494), so a
      // per-call-site emitter would double-count the drawer path, which calls
      // this function rather than reimplementing the move.
      method: TCardMoveMethod = "drag"
    ) => {
      if (!board) return;
      const card = cards.find(c => c?.player?.id === playerId);
      if (!card) {
        setError("Card not found for player");
        return;
      }

      const newPosition = calculateNewPosition(
        cards,
        toPhaseId,
        insertionIndex
      );
      const targetPhase = phases.find(p => p.id === toPhaseId);
      const phaseName = targetPhase?.name || card.kanbanPhase.name;

      // SE-5495: resolve the destination stage's gate BEFORE any side effect.
      //
      // Ordering here is load-bearing on two counts:
      //
      // 1. The prompt must settle before `mutations.moveCardMutation` fires,
      //    so a cancelled prompt performs no network write at all (AC1:
      //    "a reason prompt appears BEFORE the move completes").
      //
      // 2. It must also settle before `onMoveStart`. onMoveStart pins the card
      //    in the destination column via the SE-4272 pendingMoves override; if
      //    it ran ahead of the prompt, cancelling would strand that override
      //    and leave the card visually parked in a column it never entered —
      //    there is no onMoveEnd on the cancel path to clean it up.
      //
      // The `gateRequiresPrompt` guard keeps the ungated path free of any
      // `await`. That matters: `movePlayerToPhase` is called from inside
      // MainContainer's `flushSync`, and awaiting before onMoveStart would
      // push the optimistic override into a later frame, reintroducing the
      // SE-4272 snap-back on every ordinary drag (AC2: frictionless).
      //
      // `decision` is three-valued on purpose: `undefined` means no prompt was
      // needed, `null` means the user cancelled, an object means they answered.
      const gate = resolveStageMoveGate(targetPhase);
      const decision =
        gateRequiresPrompt(gate) && requestStageMoveDecision
          ? await requestStageMoveDecision({
              gate,
              cardId: card.id,
              playerId,
              playerName:
                card.player?.knownName ?? card.player?.name ?? undefined,
              toPhaseId,
              toPhaseName: phaseName,
              fromPhaseName: card.kanbanPhase.name,
              // SE-5496: the board template's valid outcomes drive the prompt's
              // outcome selector. Empty when the target isn't terminal.
              validOutcomes: board.validOutcomes ?? [],
            })
          : undefined;

      // Cancelled: abandon the move cleanly. No mutation, no override, and no
      // error — a deliberate cancel is not a failure.
      if (decision === null) return;

      const rationale = decision?.rationale;

      // SE-5496: a terminal target is a CLOSE, not a move. When the gate
      // resolved `requiresOutcome` and the user picked an outcome, route to the
      // dedicated close mutation (outcome + required reason + explicit target
      // phase) instead of `moveCardMutation`.
      if (gate.requiresOutcome && decision?.outcome) {
        await closeToTerminalAndReport(
          card,
          decision.outcome,
          rationale ?? "",
          toPhaseId,
          method
        );
        return;
      }

      // moveId scopes the optional cleanup in onMoveEnd — a rapid second
      // drag of the same card replaces the entry with a new moveId so the
      // first drag's cleanup cannot wipe the second's override.
      moveCounterRef.current += 1;
      const moveId = `mv-${moveCounterRef.current}`;

      // Lock the card in the destination column at the React level so that
      // even if Apollo's cache briefly reverts during optimistic-layer removal,
      // the UI never shows the card in the wrong column (SE-4272).
      //
      // On success the override is kept — Apollo emits late "ghost"
      // broadcasts after the mutation has resolved where the optimistic
      // layer is removed in one broadcast and the server response is
      // written in the next, briefly revealing the source-phase state.
      // The override masks that window. It gets replaced on the next drag
      // of the same card, so entries never leak in practice.
      //
      // On error the override is cleared so Apollo's optimistic rollback is
      // visible to the user.
      onMoveStart(card.id, toPhaseId, phaseName, moveId);

      try {
        const moveResult = await mutations.moveCardMutation({
          variables: {
            input: {
              cardId: card.id,
              targetPhaseId: toPhaseId,
              targetPosition: newPosition,
              // SE-5495: spread so an ungated move sends no `rationale` key at
              // all, rather than an explicit null the backend would record as
              // an empty reason on the audit trail.
              ...(rationale === undefined ? {} : { rationale }),
            },
          },
          // SE-5495 note on `rationale` and the optimisticResponse: `rationale`
          // is INPUT-ONLY. `KanbanCard` exposes no such field, so it never
          // enters MoveKanbanCard's selection set (KanbanCardFragment) and must
          // NOT appear below. The error-code-13 "Missing field" flood (SE-5058)
          // comes from omitting a field that IS selected — the `rank` case
          // handled below — not from omitting one that isn't.
          optimisticResponse: {
            __typename: "Mutation",
            moveKanbanCard: {
              __typename: "KanbanCard",
              ...card,
              kanbanPhase: {
                __typename: "KanbanPhase",
                id: toPhaseId,
                name: phaseName,
              },
              kanbanPhaseId: toPhaseId,
              position: newPosition,
              // SE-5493: `rank` joined KanbanCardFragment, so it is now part of
              // this mutation's selection set and MUST be present here — an
              // incomplete optimisticResponse triggers an error-code-13
              // "Missing field `rank` while writing result" flood (SE-5058).
              // Spreading `card` would carry the SOURCE phase's rank; the card
              // is landing at `newPosition` in the target phase, whose 1-based
              // rank is one greater.
              rank: newPosition + 1,
              // SE-5497: a CROSS-stage move resets the stage clock — the card
              // just entered `toPhaseId` — so days-in-stage optimistically reads
              // 0, matching what the server resolves on refetch. A same-stage
              // reorder (`toPhaseId === card.kanbanPhaseId`) must NOT reset it,
              // or it would wrongly zero elapsed days + clear stale status.
              enteredStageAt:
                card.kanbanPhaseId === toPhaseId
                  ? card.enteredStageAt
                  : new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          },
        });

        // SE-5508 `card_moved`, ordinary-move variant.
        //
        // The success signal is the PRESENCE OF DATA, not the absence of a
        // throw. `moveCardMutation` carries a hook-level `onError` (see
        // `useKanbanBoardMutations`), and Apollo RESOLVES rather than rejects
        // when one is set — so the `catch` below never runs for a failed move
        // and an emit placed after the `await` alone would report every failed
        // move as a success. This is the same defect shape the SE-5500 comment
        // in `removePlayerFromBoard` documents, on the move path.
        //
        // The optimistic response does not confuse this: `mutate()` resolves
        // with the SERVER result, and on failure that is undefined while the
        // optimistic layer is rolled back.
        //
        // SE-6278: that same resolves-rather-than-rejects behaviour also
        // stranded the `pendingMoves` override. `onMoveStart` locks the card in
        // its DESTINATION column at the React level to mask Apollo's late ghost
        // broadcasts (SE-4272), and the override is deliberately KEPT on
        // success. It was only ever released by the `catch` below — which never
        // runs here — so a failed move left the card displayed in the stage it
        // had not moved to, on top of a cache Apollo had already rolled back.
        // The user saw an error toast beside a card that had visibly moved.
        //
        // Released explicitly on the absent-data path, so the rollback becomes
        // visible. `onMoveEnd` is moveId-scoped, so a rapid second move of the
        // same card is unaffected.
        if (!moveResult?.data?.moveKanbanCard) {
          onMoveEnd(card.id, moveId);
          return;
        }

        void AnalyticsTracker.pipeline.logCardMoved({
          // `card.kanbanPhaseId` rather than the `_fromPhaseId` argument:
          // the argument is what the caller believed, this is what the card
          // actually held when the move was resolved.
          from: card.kanbanPhaseId,
          to: toPhaseId,
          method,
          // A protected stage prompts for a rationale; a terminal one is
          // handled by the close branch above. Either gate makes the move
          // "sensitive" in the PRD's sense.
          sensitive: gateRequiresPrompt(gate),
          // Only the terminal-close path produces an outcome.
          outcome: null,
          board_id: board.id,
        });
      } catch (err) {
        const release = () => onMoveEnd(card.id, moveId);
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(() => requestAnimationFrame(release));
        } else {
          setTimeout(release, 0);
        }
        throw err;
      }
    },
    [
      board,
      cards,
      phases,
      mutations,
      closeToTerminalAndReport,
      setError,
      onMoveStart,
      onMoveEnd,
      requestStageMoveDecision,
    ]
  );

  const addPlayerToBoard = useCallback(
    async (playerId: string, phaseId: string, _playerData: PlayerDragData) => {
      if (!board) return;
      const existingCard = cards.find(c => c?.player?.id === playerId);
      if (existingCard) {
        await movePlayerToPhase(playerId, existingCard.kanbanPhase.id, phaseId);
        return;
      }

      const addResult = await mutations.addPlayerMutation({
        variables: {
          input: { kanbanBoardId: board.id, playerId, kanbanPhaseId: phaseId },
        },
      });

      // SE-5508 `card_added`, drag variant. `addPlayerMutation` also carries a
      // hook-level `onError`, so — exactly as on the move path above — the
      // success signal is the presence of data, never the absence of a throw.
      // The existing-card branch above returns through `movePlayerToPhase`,
      // which emits `card_moved` instead, so a drop onto an already-carded
      // player cannot emit both.
      if (addResult?.data?.addPlayerToKanban) {
        void AnalyticsTracker.pipeline.logCardAdded({
          source: "drag",
          stage: phaseId,
          board_id: board.id,
        });
      }
      // Refetch to pick up the new card from the server.
      // The card doesn't appear via optimistic response or cache.modify
      // because the ListKanbanCards query returns a different type
      // (PlayerWithKanban) than the mutation result (KanbanCard).
      refetchCards();
    },
    [board, cards, mutations, movePlayerToPhase, refetchCards]
  );

  const removePlayerFromBoard = useCallback(
    async (playerId: string, rationale?: string) => {
      // Reject rather than resolve. This function is contracted to reject on
      // failure so the confirmation dialog can tell success from failure, and a
      // silent `return` here resolved successfully WITHOUT ever calling the
      // mutation — the dialog would close and discard the user's typed reason
      // while nothing was removed. Same defect shape as the Apollo
      // resolves-rather-than-rejects case handled below, on an earlier path.
      if (!board) {
        throw new Error(`No board available to remove player ${playerId}`);
      }
      setIsSaving(true);
      try {
        const trimmedRationale = rationale?.trim();
        const result = await mutations.removePlayerMutation({
          variables: {
            kanbanBoardId: board.id,
            playerId,
            // Send nothing rather than "" — the server normalises blank to
            // null anyway, and an explicit empty string reads on the audit row
            // as a reason that was given and was empty.
            rationale: trimmedRationale ? trimmedRationale : undefined,
          },
        });

        // `removePlayerMutation` carries a hook-level `onError` (the failure
        // toast), and Apollo RESOLVES rather than rejects when one is set — so
        // the absence of data, not a thrown error, is what marks a failed
        // removal here. Without this check a failed removal would look like a
        // success and the confirmation dialog would close on it.
        if (!result?.data?.removePlayerFromKanban) {
          throw new Error(`Failed to remove player ${playerId} from board`);
        }

        // SE-5508 `card_removed`. Placed AFTER the absent-data check above, so
        // it inherits that check's failure semantics exactly: a removal that
        // Apollo resolved-but-empty (the `onError` case) throws before
        // reaching here and emits nothing.
        //
        // Retry safety: the confirmation dialog keeps its typed reason and
        // allows another attempt after a failure. Because only a committed
        // removal reaches this line, a retried removal emits once per
        // SUCCESSFUL removal, not once per attempt.
        void AnalyticsTracker.pipeline.logCardRemoved({
          // The drag-to-remove path supplies no rationale; the drawer's
          // confirmation dialog requires one. `trimmedRationale` is the same
          // value sent to the server, so this reports what was recorded.
          reason_given: Boolean(trimmedRationale),
          board_id: board.id,
        });
      } finally {
        setIsSaving(false);
      }
    },
    [board, mutations, setIsSaving]
  );

  const handlePlayerDrop = useCallback(
    (targetPhaseId: string, playerData: PlayerDragData) => {
      if (!board) {
        setError("No board available for player drop");
        return;
      }

      const playerId = playerData.id;
      const existingCard = cards.find(c => c?.player?.id === playerId);

      // Move existing player
      if (existingCard) {
        movePlayerToPhase(
          playerId,
          existingCard.kanbanPhase.id,
          targetPhaseId,
          playerData.insertionIndex
        ).catch(() => {
          // Error handled by mutation onError
        });
        return;
      }

      // Add new player with recovery for "already exists" case
      addPlayerToBoard(playerId, targetPhaseId, playerData).catch(
        (playerExistsError: Error) => {
          const isAlreadyExists = playerExistsError?.message?.includes(
            "Player already exists"
          );
          if (!isAlreadyExists) return;

          // Recovery: refetch and move if found
          refetchCards().then(rawResult => {
            const result = rawResult as RefetchCardsResult;
            const refreshedNodes =
              result.data?.listKanbanCards?.edges?.map(edge => edge.node) || [];
            const found = refreshedNodes.find(
              node => node?.player?.id === playerId
            );
            const foundPhase = found?.kanbanCard?.kanbanPhase;
            if (foundPhase) {
              movePlayerToPhase(
                playerId,
                foundPhase.id,
                targetPhaseId,
                playerData.insertionIndex
              );
            }
          });
        }
      );
    },
    [board, cards, addPlayerToBoard, movePlayerToPhase, refetchCards, setError]
  );

  // Flush the debounced rank save for a phase: dispatch a single
  // updateKanbanCardPositions with the window's FINAL order, then clear the
  // optimistic override once the save settles (SE-4687).
  const flushRankSave = useCallback(
    (phaseId: string) => {
      const timer = rankTimerRef.current.get(phaseId);
      if (timer) {
        clearTimeout(timer);
        rankTimerRef.current.delete(phaseId);
      }

      const working = rankWorkingOrderRef.current.get(phaseId);
      const batchId = rankBatchIdRef.current.get(phaseId);
      rankWorkingOrderRef.current.delete(phaseId);
      rankBatchIdRef.current.delete(phaseId);
      if (!working || !batchId) return;

      const cardPositions = working.map((card, index) => ({
        cardId: card.id,
        position: index,
      }));

      void mutations
        .updateCardPositionsMutation({
          variables: { input: { kanbanPhaseId: phaseId, cardPositions } },
          // Complete against the UpdateKanbanCardPositions selection set
          // (KanbanCardFragment) so the settled cache matches the optimistic
          // display without an error-code-13 partial-write flood, and so
          // Apollo's rollback target on error is the pre-sequence order.
          optimisticResponse: {
            __typename: "Mutation",
            updateKanbanCardPositions: working.map((card, index) => ({
              __typename: "KanbanCard",
              id: card.id,
              position: index,
              // SE-5493: `rank` joined KanbanCardFragment and is therefore part
              // of this selection set. Omitting it would make the response
              // partial and flood error-code-13 "Missing field `rank` while
              // writing result" (SE-5058). `cardPositions` above sends the same
              // 0-based `index`; the viewer-facing rank is its 1-based twin, so
              // the optimistic cache matches what the server will resolve for
              // this viewer.
              rank: index + 1,
              notes: card.notes ?? null,
              kanbanPhaseId: phaseId,
              // SE-5497: enteredStageAt is part of KanbanCardFragment and thus
              // this selection set. A rank reorder never moves a card between
              // stages, so preserve the existing value (fallback to createdAt)
              // rather than resetting the stage clock — omitting it would flood
              // error-code-13 (SE-5058).
              enteredStageAt: card.enteredStageAt ?? card.createdAt,
              kanbanPhase: {
                __typename: "KanbanPhase",
                id: card.kanbanPhase.id,
                name: card.kanbanPhase.name,
              },
              createdAt: card.createdAt,
              updatedAt: new Date().toISOString(),
            })),
          },
        })
        .then(() => {
          // Success: the cache now holds the saved order; drop the override so
          // the cache is the single source of truth again.
          onRankEnd(batchId);
        })
        .catch(() => {
          // Failure: the mutation's onError shows a non-blocking toast and
          // Apollo rolls the optimistic layer back to the pre-sequence order.
          // Clearing the override reveals that rolled-back order (AC: revert to
          // the exact pre-drag order captured when the save was scheduled).
          onRankEnd(batchId);
        });
    },
    [mutations, onRankEnd]
  );

  const reorderCardsInPhase = useCallback(
    (phaseId: string, activePlayerId: string, overPlayerId: string) => {
      if (!board) return;
      if (activePlayerId === overPlayerId) return;

      // Continue an open debounce window if one exists so rapid drags compound
      // on the already-optimistic order; otherwise seed from the current cache
      // order. The batch mutation requires EVERY card in the phase, so the
      // working set is the full phase in ascending viewer order.
      //
      // SE-5493: the seed MUST use the same ordering key the column renders
      // with — the viewer's private `rank` on a PIPELINE board, the shared
      // `position` elsewhere. Seeding a pipeline reorder from `position` would
      // compute `oldIndex`/`newIndex` against an order the user is not looking
      // at, so the dragged card would land in the wrong slot.
      const existingWorking = rankWorkingOrderRef.current.get(phaseId);
      const working =
        existingWorking ??
        sortCardsForViewer(
          cards.filter(
            card => card?.kanbanPhase?.id === phaseId && card?.player
          ),
          board.sourceType
        );

      const oldIndex = working.findIndex(
        card => card.player?.id === activePlayerId
      );
      const newIndex = working.findIndex(
        card => card.player?.id === overPlayerId
      );
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

      // Open a new window (allocate a batch id) only when one isn't active.
      // The cache is NOT written during the window — only the optimistic
      // override — so the cache holds the pre-sequence order throughout,
      // giving rollback a stable target.
      if (!rankBatchIdRef.current.has(phaseId)) {
        rankCounterRef.current += 1;
        rankBatchIdRef.current.set(phaseId, `rk-${rankCounterRef.current}`);
      }
      const batchId = rankBatchIdRef.current.get(phaseId) as string;

      // arrayMove assigns the active card the over card's slot and shifts the
      // rest, keeping the sequence contiguous (AC: drag 3→1, drag 2→4).
      const reordered = arrayMove(working, oldIndex, newIndex);
      rankWorkingOrderRef.current.set(phaseId, reordered);

      // Apply the optimistic display synchronously. reorderCardsInPhase runs
      // inside MainContainer's flushSync, so this override commits in the same
      // frame as the drag-state clear — no snap-back (SE-4272). Only the
      // network save below is deferred.
      const entries = reordered.map((card, index) => ({
        cardId: card.id,
        position: index,
      }));
      onRankStart(entries, batchId);

      // Trailing debounce keyed by phaseId: coalesce rapid reorders into a
      // single final-state save.
      const existingTimer = rankTimerRef.current.get(phaseId);
      if (existingTimer) clearTimeout(existingTimer);
      rankTimerRef.current.set(
        phaseId,
        setTimeout(() => flushRankSave(phaseId), RANK_SAVE_DEBOUNCE_MS)
      );
    },
    [board, cards, onRankStart, flushRankSave]
  );

  // SE-5496: reopen a closed card into a chosen working stage. Refetch-based
  // reconciliation (no optimism) mirrors the close path — the mutation returns
  // a bare KanbanCard, and the reopened card must re-enter its working column.
  const reopenCard = useCallback(
    async (cardId: string, targetPhaseId: string, rationale?: string) => {
      const trimmed = rationale?.trim();
      try {
        await mutations.reopenCardMutation({
          variables: {
            input: {
              cardId,
              targetPhaseId,
              // Optional and nullable: send it only when the user typed one.
              ...(trimmed ? { rationale: trimmed } : {}),
            },
          },
        });
      } catch (err) {
        // A card that is no longer closed (CARD_NOT_CLOSED / CARD_ALREADY_CLOSED
        // races) just needs the board reconciled; a no-access signal is silent.
        await handlePipelineError(err);
      }
    },
    [mutations, handlePipelineError]
  );

  // Flush any pending rank saves on unmount so a reorder made just before
  // navigation is not lost. A ref keeps the flush current without re-running
  // the unmount effect (which would flush mid-session). The ref is written in
  // an effect, never during render.
  const flushRankSaveRef = useRef(flushRankSave);
  useEffect(() => {
    flushRankSaveRef.current = flushRankSave;
  }, [flushRankSave]);
  useEffect(() => {
    const timers = rankTimerRef.current;
    return () => {
      const phaseIds = Array.from(timers.keys());
      phaseIds.forEach(phaseId => flushRankSaveRef.current(phaseId));
    };
  }, []);

  return {
    isRosterBoard,
    playerCount,
    movePlayerToPhase,
    addPlayerToBoard,
    removePlayerFromBoard,
    handlePlayerDrop,
    reorderCardsInPhase,
    reopenCard,
  };
};

export default useKanbanCardOperations;
